import { z } from 'zod';
import { idSchema, updatedAtSchema } from './common';

/**
 * An external team (SPEC §1.1J): PGD, GRS, a club, the organising crew — a
 * group that covers posts, of which we know the group and its leader but
 * usually not the people. Its members are `volunteer` rows carrying
 * `externalGroupId`, so they occupy shifts like anyone else while staying out
 * of briefing, mail and autofill.
 *
 * The leader is a reference for coordinators, never a recipient: nothing in the
 * app writes to `leaderEmail`.
 */
const fields = {
  name: z.string().trim().min(1).max(100),
  leaderName: z.string().trim().max(200),
  leaderEmail: z.email().max(320).nullable(),
  leaderPhone: z.string().trim().max(60),
  note: z.string().max(4000),
};

export const externalGroupInputSchema = z.object({
  name: fields.name,
  leaderName: fields.leaderName.default(''),
  leaderEmail: fields.leaderEmail.default(null),
  leaderPhone: fields.leaderPhone.default(''),
  note: fields.note.default(''),
});

/**
 * Built from the defaults-free field shapes rather than
 * `externalGroupInputSchema.partial()`.
 *
 * `.partial()` makes a key optional but leaves its `.default()` in place, so an
 * absent key still parses to its default — and a PATCH of one field would then
 * carry `leaderName: ''` and `note: ''` into the update and erase them. The
 * team screen commits one field per blur, so that is not a corner case there:
 * it is every edit.
 */
export const externalGroupUpdateSchema = z
  .object(fields)
  .partial()
  .extend({ updatedAt: updatedAtSchema })
  // `updatedAt` alone would reach the database as an empty `set` and throw.
  .refine((v) => Object.keys(v).length > 1, { message: 'ni sprememb' });

export const externalGroupSchema = externalGroupInputSchema.extend({
  id: idSchema,
  active: z.boolean(),
  createdAt: z.iso.datetime({ offset: true }),
  updatedAt: updatedAtSchema,
});

/**
 * How many people the group sends. The app derives the slots from it — growing
 * adds them, shrinking retires the highest-numbered — so a coordinator states a
 * headcount rather than maintaining a list.
 */
export const externalGroupSlotsSchema = z.object({
  count: z.number().int().min(0).max(50),
});

/**
 * What a coordinator may edit on one place once the team names its people.
 * At least one field, or the update reaches the database as an empty `set`.
 */
export const externalSlotUpdateSchema = z
  .object({
    externalPersonName: z.string().trim().max(200).optional(),
    phone: z.string().trim().max(60).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'ni sprememb' });

export type ExternalGroupInput = z.infer<typeof externalGroupInputSchema>;
export type ExternalGroup = z.infer<typeof externalGroupSchema>;
export type ExternalGroupSlots = z.infer<typeof externalGroupSlotsSchema>;
export type ExternalSlotUpdate = z.infer<typeof externalSlotUpdateSchema>;
